import { useGame } from '../state/store'
import { fmt } from '../engine/decimal'
import { nextLevelCost, type LetterState } from '../engine/formulas'
import { LETTERS_BY_ID } from '../config/letters'
import { GLOBAL } from '../config/global'
import { isMasteryComplete, type LetterMastery } from '../engine/mastery'

const MILESTONES = [
  { key: 'milestoneLexicographer' as const, name: 'Lexicographer', desc: 'Used in 10 different words' },
  { key: 'milestoneLengthy' as const, name: 'Lengthy', desc: 'Used in a word of 8+ letters' },
  { key: 'milestoneRaritySeeker' as const, name: 'Rarity Seeker', desc: 'Used in a word with 2+ rare letters' },
  { key: 'milestoneChainLink' as const, name: 'Chain Link', desc: 'Part of a 3+ word combo' },
  { key: 'milestoneSwift' as const, name: 'Swift', desc: 'Combo completed within 15s' },
]

export function MasteryCard({ letter }: { letter: LetterState }) {
  const def = LETTERS_BY_ID[letter.id]
  const ink = useGame((s) => s.ink)
  const mastery = useGame((s) => s.mastery[letter.id])
  const capitalizeLetter = useGame((s) => s.capitalizeLetter)
  const levelUp = useGame((s) => s.levelUp)

  const m: LetterMastery = mastery ?? {
    wordsUsed: [],
    milestoneLexicographer: false,
    milestoneLengthy: false,
    milestoneRaritySeeker: false,
    milestoneChainLink: false,
    milestoneSwift: false,
  }

  const complete = isMasteryComplete(m)
  const capCost = nextLevelCost(def, 0).mul(GLOBAL.capRitualCostMultiplier)
  const canCapitalize = complete && !letter.capital && ink.gte(capCost)
  const levelCost = nextLevelCost(def, letter.level)
  const canLevelUp = letter.level > 0 && ink.gte(levelCost)

  return (
    <div className="rounded-md border border-ink-border bg-ink-bg p-3 mt-2">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-bold text-ink-teal">
          {letter.capital ? '★ ' : ''}MASTERY CARD — {def.letter}
        </span>
        <span className="text-[10px] text-ink-muted">
          {m.wordsUsed.length} / {GLOBAL.masteryWordsRequired} words
        </span>
      </div>
      <div className="space-y-1">
        {MILESTONES.map((ms) => {
          const achieved = m[ms.key]
          return (
            <div key={ms.key} className="flex items-center gap-2 text-[11px]">
              <span
                className={`inline-block h-2.5 w-2.5 rounded-full ${
                  achieved ? 'bg-ink-teal' : 'bg-ink-border'
                }`}
              />
              <span className={achieved ? 'text-ink-text' : 'text-ink-muted'}>
                {ms.name}
              </span>
              <span className="text-ink-muted/50 hidden sm:inline">— {ms.desc}</span>
            </div>
          )
        })}
      </div>
      <div className="mt-3 flex gap-2">
        {canLevelUp && (
          <button
            onClick={() => levelUp(letter.id)}
            className="flex-1 rounded border border-ink-tealdim px-2 py-1 text-[11px] font-bold text-ink-teal hover:shadow-glow"
          >
            Level Up
          </button>
        )}
        {letter.capital ? (
          <span className="flex-1 rounded border border-ink-border px-2 py-1 text-center text-[11px] text-ink-muted">
            Capitalized
          </span>
        ) : (
          <button
            disabled={!canCapitalize}
            onClick={() => capitalizeLetter(letter.id)}
            className={`flex-1 rounded border px-2 py-1 text-[11px] font-bold transition ${
              canCapitalize
                ? 'border-ink-teal text-ink-teal hover:shadow-glow'
                : 'border-ink-border text-ink-muted opacity-50'
            }`}
          >
            {complete ? `Capitalize (${fmt(capCost)} Ink)` : 'Capitalize'}
          </button>
        )}
      </div>
    </div>
  )
}
